// Traces (Packets > Traces): the payload shapes served by GET /api/traces and GET /api/traces/{tag} and the pure logic the
// view renders from them. NO imports on purpose -- compiled on its own and covered by node:test
// (frontend/tests/traces.test.mjs), so every rule that decides what text the page shows lives here, not in JSX.
//
// The server decides what a hop is (one node, several, none) and which tags are traces or pings; this only words it.
// A hop that is not one node is shown as such, never as a guess.

export type TraceKind = "TRACE" | "PING";
export type TraceFilter = "all" | TraceKind;
export type TraceWindow = "24h" | "7d" | "30d";
export type SnrColor = "green" | "yellow" | "red";

export const TRACE_FILTERS: { id: TraceFilter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "TRACE", label: "Multi-hop" },
  { id: "PING", label: "1 hop" },
];
export const TRACE_WINDOWS: { id: TraceWindow; label: string }[] = [
  { id: "24h", label: "24 hours" },
  { id: "7d", label: "7 days" },
  { id: "30d", label: "30 days" },
];
export const DEFAULT_WINDOW: TraceWindow = "7d";
export const PAGE_LIMIT = 50;

export const TRACES_TITLE = "Traces";
export const TRACES_DESC = "Trace packets grouped by tag. Each arrow carries the signal the repeater it points to heard the packet at.";
export const NO_TRACES_TEXT = "No traces were heard in this window.";
export const NO_MATCH_TEXT = "No traces of this type were heard in this window.";
export const NO_SEARCH_TEXT = "No trace in this window has a hop matching that name or hash.";
export const SEARCH_PLACEHOLDER = "Search by repeater name or hash";
export const SEARCH_HELP = "Finds traces where any hop is a repeater whose name contains this, or whose hash starts with it.";
export const PICK_TEXT = "Pick a trace to see every packet we heard for it.";

// One hop of a route with the signal on the link into it. A trace's repeater at hop i adds the SNR it heard the packet at,
// so reading i belongs to the link from the previous hop (the sender, for the first) to hop i. `snr_n` is how many packets
// heard the link; 0 means none did. `snr_db` is the newest reading, `snr_min` and `snr_max` span them all.
export interface TraceHop {
  hash: string;
  kind: HopKind;
  node: { id: string; name: string | null } | null;
  n_candidates: number;
  snr_db: number | null;
  snr_min: number | null;
  snr_max: number | null;
  snr_n: number;
  snr_color: SnrColor | null;
}

export interface TraceItem {
  tag: string;
  tag_number: number;
  kind: TraceKind;
  packets: number;
  observers: number;
  scopes: string[];
  route_types: string[];
  route_packets: number;
  sources: { radio: number; corescope: number };
  first_heard: string;
  last_heard: string;
  hash_size: number;
  reached: number;
  hops: TraceHop[];
}

export interface SnrPolicy {
  yellow_from_db: number;
  green_from_db: number;
}

export interface TraceList {
  snr_policy: SnrPolicy;
  window: TraceWindow;
  type: TraceFilter;
  q: string | null;
  counts: { all: number; TRACE: number; PING: number };
  items: TraceItem[];
  next_cursor: string | null;
  unreadable: number;
  duplicates_dropped: number;
}

export interface TraceNode {
  lat?: number | null;
  lon?: number | null;
  id: string;
  name: string | null;
  role: string | null;
  last_heard: string | null;
  has_position: boolean;
}

export type HopKind = "unique" | "path" | "ambiguous" | "impossible" | "none";

export interface DetailHop {
  snr_min?: number | null;
  snr_max?: number | null;
  snr_n?: number;
  hash: string;
  kind: HopKind;
  node: TraceNode | null;
  candidates: TraceNode[];
  n_candidates: number;
  reached: boolean;
  snr_db: number | null;
  snr_color: SnrColor | null;
}

export interface TracePacketRow {
  id: string;
  wire: string | null;
  source: "radio" | "mqtt" | "corescope" | "other";
  source_label: string;
  observer: string | null;
  observer_snr: number | null;
  observer_rssi: number | null;
  route_type: string;
  scope: string | null;
  first_heard: string;
  last_heard: string;
  n_observers: number;
  observers: string[];
  reached: number;
  hash_size: number;
  hops: DetailHop[];
  // The packet feed's row for this packet (what the packet panel opens from); null when it could not be read.
  packet: Record<string, unknown> | null;
}

export interface TraceDetail {
  tag: string;
  tag_number: number;
  kind: TraceKind;
  window: TraceWindow;
  packets: TracePacketRow[];
  first_heard: string;
  last_heard: string;
  route: DetailHop[];
  route_packets: number;
  hash_size: number;
  route_types: string[];
  scopes: string[];
  snr_policy: SnrPolicy;
  path_context: boolean;
}

export interface TraceOfPacket {
  tag: string;
  window: TraceWindow | null;
}

// --- requests ----------------------------------------------------------------------------------------------------

/** A trace tag is 8 hex digits. */
export function isTag(text: string | null | undefined): text is string {
  return typeof text === "string" && /^[0-9a-f]{8}$/i.test(text);
}

export function isWindow(text: string | null | undefined): text is TraceWindow {
  return text === "24h" || text === "7d" || text === "30d";
}

export function listPath(filter: TraceFilter, window: TraceWindow, cursor: string | null, limit = PAGE_LIMIT, search = ""): string {
  const q = [`type=${filter}`, `window=${window}`, `limit=${limit}`];
  if (cursor) q.push(`before=${encodeURIComponent(cursor)}`);
  if (search.trim()) q.push(`q=${encodeURIComponent(search.trim())}`);
  return `/api/traces?${q.join("&")}`;
}

export function detailPath(tag: string, window: TraceWindow): string {
  return `/api/traces/${encodeURIComponent(tag.toLowerCase())}?window=${window}`;
}

export function forPacketPath(packetId: string | number): string {
  return `/api/traces/for-packet/${encodeURIComponent(String(packetId))}`;
}

/** Where the Traces tab opens on one tag. */
export function tracesHash(tag?: string | null, window?: TraceWindow | null): string {
  if (!isTag(tag)) return "#/packets/traces";
  return `#/packets/traces?tag=${tag.toLowerCase()}${isWindow(window) ? `&window=${window}` : ""}`;
}

/** The list after one more page: items already shown are kept, a tag the new page repeats is not shown twice. */
export function appendPage(have: TraceItem[], more: TraceItem[]): TraceItem[] {
  const seen = new Set(have.map((i) => i.tag));
  return [...have, ...more.filter((i) => !seen.has(i.tag))];
}

/** One plain line for a failed request. */
export function errorText(status: number | undefined): string {
  if (status === 401) return "Your session ended. Sign in again to see traces.";
  if (status === 403) return "Your account can't see traces.";
  if (status === 404) return "Traces aren't available on this server.";
  if (status === undefined) return "Couldn't reach the server. Try again.";
  return "Traces could not be loaded. Try again.";
}

// --- wording ------------------------------------------------------------------------------------------------------

/** SNR in dB, up to two decimals (the radio reports quarters), with a real minus sign. Null is a dash. */
export function snrText(db: number | null | undefined): string {
  if (db === null || db === undefined || Number.isNaN(db)) return "—";
  const s = String(Math.round(Math.abs(db) * 100) / 100);
  return `${db < 0 ? "−" : ""}${s} dB`;
}

export function snrShort(db: number | null | undefined): string {
  return snrText(db).replace(" dB", "");
}

export function snrColorName(c: SnrColor | null): string {
  return c === "green" ? "good" : c === "yellow" ? "fair" : c === "red" ? "weak" : "not heard";
}

export function packetsText(n: number): string {
  return n === 1 ? "1 packet" : `${n} packets`;
}

export function observersText(n: number): string {
  return n === 1 ? "1 observer" : `${n} observers`;
}

export function hopsText(n: number): string {
  return n === 1 ? "1 hop" : `${n} hops`;
}

export function filterCount(counts: TraceList["counts"] | null, f: TraceFilter): string {
  return counts ? String(counts[f]) : "";
}

/** The line under a tag in the list. */
export function cardLine(i: Pick<TraceItem, "packets" | "observers" | "scopes" | "hops">): string {
  const parts = [packetsText(i.packets), observersText(i.observers), hopsText(i.hops.length)];
  if (i.scopes.length > 0) parts.push(i.scopes.length === 1 ? `scope ${i.scopes[0]}` : `${i.scopes.length} scopes`);
  return parts.join(", ");
}

/** How far a packet got along its route: "2 of 3 hops heard". */
export function progressText(reached: number, total: number): string {
  if (total === 0) return "No hops";
  if (reached >= total) return `All ${hopsText(total)} heard`;
  if (reached === 0) return `No repeater had relayed it yet, ${hopsText(total)} to go`;
  return `${reached} of ${hopsText(total)} heard`;
}

export function scopeText(scope: string | null): string {
  return scope ? `Scope ${scope}` : "No scope";
}

/** What a hop is called: the node's name when one node, otherwise its hash. */
export function hopLabel(h: Pick<DetailHop, "hash" | "node">): string {
  return h.node?.name || h.hash;
}

/** One plain sentence for a hop that is not one node. Empty for a hop that is. */
export function hopNote(h: Pick<DetailHop, "hash" | "kind" | "n_candidates">): string {
  if (h.kind === "unique" || h.kind === "path") return "";
  if (h.kind === "ambiguous") return `${h.n_candidates} nodes start with ${h.hash}. None is picked.`;
  if (h.kind === "impossible") return `No consistent answer for ${h.hash} on this route. Left unresolved.`;
  return `No node we know starts with ${h.hash}.`;
}

export function hashWidthText(n: number): string {
  return `${n}-byte hashes`;
}

/** The small line under a chip that is not one node: how wide its hash is and why it has no name. */
export function hopStateWord(k: HopKind, size: number): string {
  const w = `${size}-byte`;
  return k === "unique" ? "One node" : k === "path" ? "Settled by the path" : k === "ambiguous" ? `${w}, ambiguous` : k === "impossible" ? `${w}, impossible` : `${w}, unknown`;
}

/** A node's name with its hash under it: name when one node, else the hash. */
export function chipTop(h: { hash: string; node: { name: string | null } | null }): string {
  return h.node?.name || h.hash;
}

/** The kind of tag in words an operator uses: more than one hop, or a single hop. */
export function kindLabel(k: TraceKind): string {
  return k === "TRACE" ? "Multi-hop" : "1 hop";
}

/** What the link into a hop says: the newest reading, the lowest to highest when packets disagree, or not heard. */
export function linkText(h: { snr_db: number | null; snr_min: number | null; snr_max: number | null; snr_n: number }): string {
  if (h.snr_n === 0 || h.snr_db === null) return "not heard";
  if (h.snr_min !== null && h.snr_max !== null && h.snr_min !== h.snr_max) return `${snrShort(h.snr_min)} to ${snrShort(h.snr_max)} dB`;
  return snrText(h.snr_db);
}

export function linkTitle(h: { snr_db: number | null; snr_min: number | null; snr_max: number | null; snr_n: number }): string {
  if (h.snr_n === 0 || h.snr_db === null) return "No packet we heard had reached this repeater, so this link has no reading.";
  if (h.snr_min !== h.snr_max && h.snr_n > 1) return `Newest reading ${snrText(h.snr_db)}. ${h.snr_n} packets heard this link, from ${snrText(h.snr_min)} to ${snrText(h.snr_max)}.`;
  return `The repeater this arrow points to heard the packet at ${snrText(h.snr_db)}.`;
}

/** A packet's own hops as links: one reading when it got that far, none when it had not. */
export function packetLinks(hops: DetailHop[]): TraceHop[] {
  return hops.map((h) => ({
    hash: h.hash, kind: h.kind, node: h.node, n_candidates: h.n_candidates,
    snr_db: h.reached ? h.snr_db : null, snr_min: h.reached ? h.snr_db : null, snr_max: h.reached ? h.snr_db : null,
    snr_n: h.reached && h.snr_db !== null ? 1 : 0, snr_color: h.reached ? h.snr_color : null,
  }));
}

/** How the Route block at the top of a trace was made. */
export function routeNote(packets: number): string {
  return packets <= 1
    ? "From the one packet we heard."
    : `Combined from ${packets} packets. Each link shows the newest reading; where packets disagree it shows the lowest to the highest.`;
}

/** Whether a field differs between the packets of a tag (and so is worth showing on each card). */
export function varies(values: (string | null)[]): boolean {
  return new Set(values.map((v) => v ?? "")).size > 1;
}

function dbWord(n: number): string {
  return snrText(n);
}

/** The one-line key to the dot colours, with the app's own thresholds. */
export function snrKey(p: SnrPolicy | null | undefined): string {
  if (!p) return "";
  return `Signal dots: green ${dbWord(p.green_from_db)} or better, yellow ${dbWord(p.yellow_from_db)} to just under ${dbWord(p.green_from_db)}, red under ${dbWord(p.yellow_from_db)}.`;
}

/** The full local date and time, for a tooltip. */
export function absoluteTime(ts: string | null | undefined): string {
  if (!ts) return "";
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString();
}

export function sourceText(p: Pick<TracePacketRow, "source" | "source_label" | "observer">): string {
  return p.source === "corescope" ? "CoreScope" : p.source_label || "Our radio";
}

/** Who heard a packet, and the signal at which that observer heard it. */
export function heardByText(p: Pick<TracePacketRow, "observer" | "observer_snr" | "n_observers">): string {
  const who = p.observer ?? "An observer";
  const extra = p.n_observers > 1 ? ` and ${p.n_observers - 1} more ${p.n_observers - 1 === 1 ? "observer" : "observers"}` : "";
  const base = `Heard by ${who}${extra}.`;
  return p.observer_snr === null || p.observer_snr === undefined ? base : `${base} Observer heard it at ${snrText(p.observer_snr)}.`;
}

/** The packet row a trace packet opens, or null when the server could not read it. */
export function openablePacket(p: Pick<TracePacketRow, "packet">): Record<string, unknown> | null {
  return p.packet && typeof p.packet === "object" ? p.packet : null;
}

/** Whether a packet's own panel offers "Show this trace": a MeshCore packet the feed calls TRACE. */
export function offersTrace(packet: { protocol?: string | null; type?: string | null } | null | undefined): boolean {
  return !!packet && packet.protocol === "meshcore" && packet.type === "TRACE";
}

/** The link target for a packet's trace answer, or null when no window reaches it. */
export function traceLinkHash(r: TraceOfPacket | null): string | null {
  return r && r.window ? tracesHash(r.tag, r.window) : null;
}

/** The note under a route when it has hops several nodes share and the path evidence that narrows them is not ready. */
export function evidenceNote(pathContext: boolean, route: Pick<DetailHop, "kind">[]): string {
  if (pathContext || !route.some((h) => h.kind === "ambiguous")) return "";
  return "Hops that several nodes share stay unclear until the path evidence is ready.";
}

// --- one link over time (GET /api/traces/links) -------------------------------------------------------------------------

export interface LinkReading {
  t: string;
  snr: number;
  color: SnrColor | null;
}

export interface LinkSeries {
  n: number;
  min: number | null;
  median: number | null;
  max: number | null;
  left_out: number;
  readings: LinkReading[];
}

export interface LinkEnd {
  id: string;
  name: string | null;
  sender: boolean;
}

export interface LinkHistory {
  from: LinkEnd;
  to: LinkEnd;
  window: TraceWindow;
  since: string;
  forward: LinkSeries;
  reverse: LinkSeries | null;
  snr_policy: SnrPolicy;
}

/** The two ends of a link: "sender" for the first link of a trace, otherwise node ids. */
export interface LinkRef {
  from: string;
  to: string;
}

/** The link into hop i of a route, when both ends are one resolved node (the sender counts for the first link). */
export function linkRef(hops: { node: { id: string } | null }[], i: number): LinkRef | null {
  const to = hops[i]?.node;
  if (!to) return null;
  if (i === 0) return { from: "sender", to: to.id };
  const from = hops[i - 1]?.node;
  return from ? { from: from.id, to: to.id } : null;
}

export function linkHistoryPath(r: LinkRef, window: TraceWindow): string {
  return `/api/traces/links?from=${encodeURIComponent(r.from)}&to=${encodeURIComponent(r.to)}&window=${window}`;
}

export const LINK_TITLE = "This link over time";
export const LINK_HELP =
  "Every reading for this pair of repeaters in the window, across all traces. Each trace route and link counts once. " +
  "The way back is shown on its own, so out and back can be compared.";
export const LINK_NEEDS_NODES = "Both ends of a link must be one known repeater to see its history.";

/** The name of one end of a link. */
export function endName(e: LinkEnd): string {
  return e.sender ? "Sender" : e.name || e.id.slice(0, 8);
}

/** One line of numbers for a series. */
export function seriesLine(s: LinkSeries): string {
  if (s.n === 0 || s.min === null || s.median === null || s.max === null) return "No readings in this window.";
  return `${s.n === 1 ? "1 reading" : `${s.n} readings`}. Lowest ${snrText(s.min)}, median ${snrText(s.median)}, highest ${snrText(s.max)}.`;
}

export function leftOutText(n: number): string {
  if (n <= 0) return "";
  return `${n === 1 ? "1 reading was" : `${n} readings were`} left out because a hop in its route was unclear.`;
}

export interface ChartBox {
  w: number;
  h: number;
  left: number;
  right: number;
  top: number;
  bottom: number;
}

/** A shared vertical range for several series so out and back can be compared, padded and at least 4 dB tall. */
export function chartRange(series: (LinkSeries | null)[]): { lo: number; hi: number } {
  const v = series.flatMap((s) => (s ? s.readings.map((r) => r.snr) : []));
  if (v.length === 0) return { lo: -10, hi: 10 };
  let lo = Math.floor(Math.min(...v) - 1);
  let hi = Math.ceil(Math.max(...v) + 1);
  if (hi - lo < 4) {
    const mid = (hi + lo) / 2;
    lo = Math.floor(mid - 2);
    hi = Math.ceil(mid + 2);
  }
  return { lo, hi };
}

export interface ChartPoint {
  x: number;
  y: number;
  snr: number;
  t: string;
  color: SnrColor | null;
}

/** Where each reading sits in the box: time left to right over [t0, t1], SNR bottom to top over [lo, hi]. */
export function chartPoints(readings: LinkReading[], box: ChartBox, t0: number, t1: number, lo: number, hi: number): ChartPoint[] {
  const pw = box.w - box.left - box.right;
  const ph = box.h - box.top - box.bottom;
  const span = Math.max(1, t1 - t0);
  const vspan = Math.max(0.0001, hi - lo);
  return readings.map((r) => {
    const t = Date.parse(r.t);
    const fx = Math.min(1, Math.max(0, (t - t0) / span));
    const fy = Math.min(1, Math.max(0, (r.snr - lo) / vspan));
    return { x: box.left + fx * pw, y: box.top + (1 - fy) * ph, snr: r.snr, t: r.t, color: r.color };
  });
}

/** The y position of a value in the same box (for the median line). */
export function chartY(v: number, box: ChartBox, lo: number, hi: number): number {
  const ph = box.h - box.top - box.bottom;
  return box.top + (1 - Math.min(1, Math.max(0, (v - lo) / Math.max(0.0001, hi - lo)))) * ph;
}

/** Axis words for the window under the chart. */
export function windowWords(w: TraceWindow): [string, string] {
  return [w === "24h" ? "24 hours ago" : w === "30d" ? "30 days ago" : "7 days ago", "now"];
}

// --- the route on a map ---------------------------------------------------------------------------------------------------

export interface MapHop {
  hash: string;
  kind: HopKind;
  node: { id: string; name: string | null; lat?: number | null; lon?: number | null } | null;
  snr_db: number | null;
  snr_min: number | null;
  snr_max: number | null;
  snr_n: number;
  snr_color: SnrColor | null;
}

export interface MapPoint {
  id: string;
  name: string | null;
  lat: number;
  lon: number;
}

/** One direction of a drawn link: the reading on the way from one node to the next. */
export interface MapDir {
  from: string;
  to: string;
  text: string;
  snr_db: number | null;
  snr_color: SnrColor | null;
}

export interface MapSeg {
  a: MapPoint;
  b: MapPoint;
  dirs: MapDir[];
  color: SnrColor | null;
}

export interface MapOmitted {
  hash: string;
  reason: "unclear" | "unknown" | "no position";
}

export interface MapPlan {
  points: MapPoint[];
  segments: MapSeg[];
  omitted: MapOmitted[];
}

function placed(h: MapHop): MapPoint | null {
  const n = h.node;
  if (!n || typeof n.lat !== "number" || typeof n.lon !== "number") return null;
  return { id: n.id, name: n.name, lat: n.lat, lon: n.lon };
}

/** What to draw for a combined route. A hop is a dot only when it is one node with a known position; a link is a line only
 *  when both its ends are dots. An out-and-back route puts the same two nodes on one line that carries both readings.
 *  Everything left off is listed with the reason. */
export function mapPlan(route: MapHop[]): MapPlan {
  const points = new Map<string, MapPoint>();
  const omitted: MapOmitted[] = [];
  const segs = new Map<string, MapSeg>();
  route.forEach((h, i) => {
    const p = placed(h);
    if (p) {
      points.set(p.id, p);
    } else if (!omitted.some((o) => o.hash === h.hash)) {
      omitted.push({ hash: h.hash, reason: h.node ? "no position" : h.kind === "none" ? "unknown" : "unclear" });
    }
    const prev = i > 0 ? placed(route[i - 1]) : null;
    if (!p || !prev || prev.id === p.id) return;
    const key = [prev.id, p.id].sort().join("|");
    let seg = segs.get(key);
    if (!seg) {
      seg = { a: prev, b: p, dirs: [], color: null };
      segs.set(key, seg);
    }
    if (!seg.dirs.some((d) => d.from === prev.id && d.to === p.id)) {
      seg.dirs.push({ from: prev.id, to: p.id, text: linkText(h), snr_db: h.snr_n > 0 ? h.snr_db : null, snr_color: h.snr_n > 0 ? h.snr_color : null });
    }
  });
  const rank: Record<SnrColor, number> = { red: 0, yellow: 1, green: 2 };
  const out = Array.from(segs.values());
  for (const s of out) {
    const heard = s.dirs.filter((d) => d.snr_color !== null);
    s.color = heard.length === 0 ? null : heard.reduce((w, d) => (rank[d.snr_color as SnrColor] < rank[w.snr_color as SnrColor] ? d : w)).snr_color;
  }
  return { points: Array.from(points.values()), segments: out, omitted };
}

/** The text for a line's hover: each direction with its reading (an out-and-back line has two). */
export function segText(s: MapSeg): string {
  const name = (id: string) => (id === s.a.id ? s.a : s.b).name || id.slice(0, 8);
  return s.dirs.map((d) => `${name(d.from)} → ${name(d.to)}: ${d.text}`).join("\n");
}

const REASON_WORDS: Record<MapOmitted["reason"], string> = {
  unclear: "several nodes share the hash",
  unknown: "no known node has the hash",
  "no position": "no position is known",
};

/** The note under the map naming what was left off and why. Empty when nothing was. */
export function omittedNote(o: MapOmitted[]): string {
  if (o.length === 0) return "";
  const by = new Map<MapOmitted["reason"], string[]>();
  for (const x of o) by.set(x.reason, [...(by.get(x.reason) ?? []), x.hash]);
  const parts = Array.from(by.entries()).map(([r, hs]) => `${hs.join(", ")} (${REASON_WORDS[r]})`);
  return `Left off the map: ${parts.join("; ")}.`;
}

export const MAP_EMPTY_TEXT = "No hop in this route has a known position, so there is nothing to draw.";
export const MAP_TITLE = "Route on the map";
